import { c as _c } from "react/compiler-runtime";
import clsxm from '../utils/style';
export interface SparklineProps {
  /** The series, oldest first. Fewer than two values renders nothing. */
  values: number[];
  /**
   * Fixed y-range. Without it the line is scaled to its own min and max, which makes a flat
   * series look volatile and two sparklines side by side incomparable — pass a domain whenever
   * the values have a meaningful scale (a percentage, a fraction of a limit).
   */
  domain?: [number, number];
  /** Draws a horizontal rule at this y-value, e.g. `0` for a signed series. */
  baseline?: number;
  height?: number;
  width?: number;
  /** Any CSS color; defaults to the chart-2 token so it matches full-size charts. */
  color?: string;
  className?: string;
  /** Describes the trend for screen readers, since the marks themselves carry no text. */
  label?: string;
}

/**
 * A minimal inline trend line, for a table cell or beside a stat.
 *
 * Deliberately not a recharts chart: it takes no axes, ticks, grid, tooltip or legend, and it
 * renders as a plain fixed-size SVG so a list of them costs nothing to measure or lay out. When a
 * reader needs to know the values rather than just the shape, that's a real chart — see
 * `ChartContainer`.
 *
 * The current value is not labeled here. A sparkline is the shape; pair it with the value as text
 * at the call site, which is what keeps it readable without color or hover.
 */
export function Sparkline(t0) {
  const $ = _c(43);
  const {
    baseline,
    className,
    color: t1,
    domain,
    height: t2,
    label,
    values,
    width: t3
  } = t0;
  const color = t1 === undefined ? "var(--color-chart-2)" : t1;
  const height = t2 === undefined ? 24 : t2;
  const width = t3 === undefined ? 88 : t3;
  if (values.length < 2) {
    return null;
  }
  let t4;
  if ($[0] !== domain || $[1] !== values) {
    t4 = domain ?? [Math.min(...values), Math.max(...values)];
    $[0] = domain;
    $[1] = values;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [min, max] = t4;
  const span = max - min || 1;
  const plotHeight = height - 2;
  let t5;
  if ($[3] !== values.length || $[4] !== width) {
    t5 = index => index / (values.length - 1) * width;
    $[3] = values.length;
    $[4] = width;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const x = t5;
  let t6;
  if ($[6] !== min || $[7] !== plotHeight || $[8] !== span) {
    t6 = value => 1 + plotHeight - (value - min) / span * plotHeight;
    $[6] = min;
    $[7] = plotHeight;
    $[8] = span;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const y = t6;
  let t7;
  if ($[10] !== values || $[11] !== x || $[12] !== y) {
    let t8;
    if ($[14] !== x || $[15] !== y) {
      t8 = (value_0, index_0) => `${x(index_0).toFixed(2)},${y(value_0).toFixed(2)}`;
      $[14] = x;
      $[15] = y;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = values.map(t8).join(" ");
    $[10] = values;
    $[11] = x;
    $[12] = y;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const points = t7;
  let t8;
  if ($[17] !== className) {
    t8 = clsxm("overflow-visible", className);
    $[17] = className;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = label === undefined;
  const t10 = label === undefined ? undefined : "img";
  const t11 = `0 0 ${width} ${height}`;
  let t12;
  if ($[19] !== baseline || $[20] !== max || $[21] !== min || $[22] !== width || $[23] !== y) {
    t12 = baseline !== undefined && baseline >= min && baseline <= max && <line stroke="var(--color-border-primary)" strokeWidth={1} x1={0} x2={width} y1={y(baseline)} y2={y(baseline)} />;
    $[19] = baseline;
    $[20] = max;
    $[21] = min;
    $[22] = width;
    $[23] = y;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== color || $[26] !== points) {
    t13 = <polyline fill="none" points={points} stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />;
    $[25] = color;
    $[26] = points;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = x(values.length - 1);
  const t15 = y(values[values.length - 1] ?? 0);
  let t16;
  if ($[28] !== color || $[29] !== t14 || $[30] !== t15) {
    t16 = <circle cx={t14} cy={t15} fill={color} r={2} />;
    $[28] = color;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== height || $[33] !== label || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t16 || $[39] !== t8 || $[40] !== t9 || $[41] !== width) {
    t17 = <svg className={t8} aria-hidden={t9} aria-label={label} height={height} role={t10} viewBox={t11} width={width}>{t12}{t13}{t16}</svg>;
    $[32] = height;
    $[33] = label;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t16;
    $[39] = t8;
    $[40] = t9;
    $[41] = width;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
}